import { FormBuilder, Validators } from '@angular/forms';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ArticleModel, ArticlesService, JointRuleModel, JointSectionModel, QueryParams, RulesService, SystemModel, SystemModelPagedResponseModel, SystemsService, PolylineModel, PolylineSegmentModel, PolylineNodeModel, Point2dModel } from 'src/app/api';
import { AuthService } from 'src/app/shared/auth-service/auth.service';
import { AppToastService } from 'src/app/shared/toast-service/toast-service';


@Component({
  selector: 'app-joint-rules-edit',
  templateUrl: './joint-rules-edit.component.html',
  styleUrl: './joint-rules-edit.component.scss',
  standalone: false,
})
export class JointRulesEditComponent {
  private destroy$ = new Subject<void>();
  
  
      // Component state
      loading = false;
      loggedIn = false;
      rule: JointRuleModel | null = null;
      positionX: number = 0;
      positionY: number = 0;
      positionZ: number = 0;
      scaleX: number = 1;
      scaleY: number = 1;
      rotation: number = 0;
      isMirrored: boolean = false;
      imageUrl: string = '';
      editingState: { [key: string]: boolean } = {};
      originalValues: { [key: string]: any } = {};
      editingField: string | null = null;
      showArticleModal = false;
      showJointSectionModal = false;
      showPolylineModal = false;
      articles: ArticleModel[] = [];
      selectedJointSection: JointSectionModel | null = null;
      isAddingArticle = false;
      currentPolyline: PolylineModel | null = null;
      isAddingSegment = false;
      isAddingNode = false;
      newSegment: PolylineSegmentModel = { startPoint: { x: 0, y: 0 }, endPoint: { x: 0, y: 0 }, length: 0 };
      newNode: PolylineNodeModel = { point: { x: 0, y: 0 }, index: 0 };
      systems: SystemModel[] = [];
      errorMessage: string = '';
      selectedSystem: SystemModel | null = null;
      currentJointSection: JointSectionModel | null = null;
      isNewJointSection = false; // true when adding new cross-section
      
      // Sorting
      sortColumn: string = '';
      sortDirection: 'asc' | 'desc' = 'asc';
  
      newArticle: ArticleModel = {
          id: '',
          articleName: '',
          material: '',
          type: ''
      };
  
      constructor(
          private route: ActivatedRoute,
          private router: Router,
          private rulesService: RulesService,
          private toastService: AppToastService,
          private authService: AuthService,
          private cdr: ChangeDetectorRef,
          private articlesService: ArticlesService,
          private systemsService: SystemsService,
          private fb: FormBuilder
      ) { }
  
      ngOnInit(): void {
          this.loggedIn = !!this.authService.getToken();
  
          // Get rule ID from route
          this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
              const ruleId = params['id'];
              if (ruleId) {
                  this.loadRule(ruleId);
              } else {
                  this.toastService.show('Rule ID is required', {
                      classname: 'bg-danger text-white',
                      delay: 3000
                  });
                  this.router.navigate(['/rules/joint']);
              }
          });
      }
  
      ngOnDestroy(): void {
          this.destroy$.next();
          this.destroy$.complete();
      }
  
      logout(): void {
          this.authService.logout();
      }
  
      loadRule(id: string): void {
          this.loading = true;
          this.rulesService.getJointRuleById(id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                  next: (rule: any) => {
                      console.log('Loaded rule in edit page', rule);
                      // Always transform to ensure nested objects are properly converted
                      // Check if transformation is needed (either top-level or nested PascalCase)
                      const needsTransformation = rule && (
                          (rule as any).Joints || // Top-level PascalCase
                          (rule.Joints && rule.Joints.some((s: any) =>
                              s.JointSections || s.ruleId || s.Description || s.ImageUrl ||
                              (s.jointSections && s.jointSections.some((cs: any) =>
                                  cs.ArticleIds || cs.Articles || cs.Material || cs.Type || cs.Description
                              ))
                          ))
                      );
                      console.log('Needs transformation:', needsTransformation);
                      // Always transform rule into camelCase
                      if (rule && typeof rule === 'object') {
                          rule = this.transformRuleToCamelCase(rule);
                      }
  
                      this.rule = rule;
                      console.log('transformed rule in edit page', this.rule);
                      this.loading = false;
                      // Force change detection to ensure view updates
                      this.cdr.detectChanges();
                  },
                  error: (error: any) => {
                      console.error('Error loading rule:', error);
                      this.toastService.show('Error loading rule', {
                          classname: 'bg-danger text-white',
                          delay: 3000
                      });
                      this.loading = false;
                      this.router.navigate(['/rules/mating']);
                  }
              });
      }
  
      goBack(): void {
          this.router.navigate(['/rules/joint']);
      }
  
      hasJointSections(section: JointRuleModel): boolean {
          return !!(section.jointSections && section.jointSections.length > 0);
      }
  
      hasArticleIds(crossSection: JointSectionModel | null | undefined): boolean {
          if (!crossSection) return false;
          const hasIds = !!(crossSection.articleIds && Array.isArray(crossSection.articleIds) && crossSection.articleIds.length > 0);
          const hasArticles = !!(crossSection.articles && Array.isArray(crossSection.articles) && crossSection.articles.length > 0);
          return hasIds || hasArticles;
      }
  
      getArticleIdsDisplay(crossSection: JointSectionModel | null | undefined): string {
          if (!crossSection) return 'None';
  
          // First try to use articleIds if available
          if (crossSection.articleIds && Array.isArray(crossSection.articleIds) && crossSection.articleIds.length > 0) {
              const validIds = crossSection.articleIds.filter(id => id != null && id !== '');
              if (validIds.length > 0) {
                  return validIds.join(', ');
              }
          }
  
          // Fallback: extract IDs from articles array if articleIds is not populated
          if (crossSection.articles && Array.isArray(crossSection.articles) && crossSection.articles.length > 0) {
              const ids = crossSection.articles
                  .map(article => article?.id || article?.articleName || null)
                  .filter(id => id != null && id !== '');
              if (ids.length > 0) {
                  return ids.join(', ');
              }
          }
  
          return 'None';
      }
  
      getArticleIdsArray(crossSection: JointSectionModel | null | undefined): string[] {
          if (!crossSection) return [];
  
          // First try to use articleIds if available
          if (crossSection.articleIds && Array.isArray(crossSection.articleIds) && crossSection.articleIds.length > 0) {
              const validIds = crossSection.articleIds.filter((id): id is string => id != null && id !== '');
              if (validIds.length > 0) {
                  return validIds;
              }
          }
  
          // Fallback: extract IDs from articles array if articleIds is not populated
          if (crossSection.articles && Array.isArray(crossSection.articles) && crossSection.articles.length > 0) {
              const ids = crossSection.articles
                  .map(article => article?.id || article?.articleName || null)
                  .filter((id): id is string => id != null && id !== '');
              if (ids.length > 0) {
                  return ids;
              }
          }
  
          return [];
      }
  
      transformationData: string = '';
      // currentCrossSection: CrossSectionModel | null = null;
      showTransformationModal = false;
  
      
  
     
  
      private mapPayloadForApi(rule: JointRuleModel): any {
          return {
              id: rule.id || '',
              ruleId: rule.ruleId || '',
              name: rule.name || '',
              description: rule.description || '',
              systemId: rule.systemId || '',
              jointSections: (rule.jointSections || []).map(section => ({
                  id: section.id || '',
                  description: section.description || '',
                  material: section.material || '',
                  type: section.type || '',
                  imageUrl: section.imageUrl || '',
                  // Map articles: preserve id if present, send articleName/material/type
                  articles: (section.articles || []).map((a: any) => ({
                      id: a.Id || a.id || '',
                      articleName: a.ArticleName || a.articleName || '',
                      material: a.Material || a.material || '',
                      type: a.Type || a.type || '',
                      systemIds: a.SystemIds || a.systemIds || [],
                      systems: a.Systems || a.systems || []
                  })),
                  articleIds: section.articleIds || []
              })),
              polyline: rule.polyline ? {
                  segments: rule.polyline.segments || [],
                  nodes: rule.polyline.nodes || [],
                  totalLength: rule.polyline.totalLength || 0,
                  articleIds: rule.polyline.articleIds || [],
                  segmentCount: rule.polyline.segmentCount || 0,
                  nodeCount: rule.polyline.nodeCount || 0,
                  isClosed: rule.polyline.isClosed || false
              } : undefined,
              createdAt: rule.createdAt,
              updatedAt: rule.updatedAt
          };
      }
  
      saveRule(): void {
        console.log('Entering saveRule Method', this.rule);
          if (!this.rule || !this.rule.id) {
              this.toastService.show('No rule to save', { classname: 'bg-warning text-light', delay: 2000 });
              return;
          }
  
          this.selectedSystem = this.systems.find(s => s.id === this.rule?.systemId) || null;          
  
          this.loading = true;
          console.log('Saving rule in saveRule Method', this.rule);
          const apiPayload = this.mapPayloadForApi(this.rule);
  
          console.log('API Payload being sent:', JSON.stringify(apiPayload, null, 2));
  
          this.rulesService.updateJointRule(this.rule.id, apiPayload).subscribe({
              next: (response: any) => {
                  console.log('Rule update response:', response);
                  if (this.rule?.systemId) {
  
                      // Ensure array exists
                      if (this.selectedSystem && this.selectedSystem.id) {
                          if (!this.selectedSystem.jointRules) {
                              this.selectedSystem.jointRules = [];
                          }
                          // Add the new rule to the system
                          this.selectedSystem.jointRules.push(response);
                          this.systemsService.updateSystem(this.selectedSystem.id, this.selectedSystem)
                              .pipe(takeUntil(this.destroy$))
                              .subscribe({
                                  next: (response) => {
  
                                  },
                                  error: (error) => {
  
                                  }
                              });
                      }
  
  
                  } else {
                      console.warn('No system id provided; skipping system update.');
                  }
                  this.toastService.show('Rule updated successfully', {
                      classname: 'bg-success text-light',
                      delay: 2000
                  });
  
  
                  this.loading = false;
  
                  // Transform response and update the rule with the response from backend
                  if (response) {
                      this.rule = this.transformRuleToCamelCase(response);
                      console.log('Rule updated with response:', this.rule);
  
                      // Re-find and update currentCrossSection to match the updated rule
                      if (this.currentJointSection && this.rule.jointSections) {
                          const updatedSection = this.rule.jointSections.find(
                              s => s.id === this.currentJointSection?.id
                          );
                          if (updatedSection) {
                              this.currentJointSection = updatedSection;
                              console.log('CurrentCrossSection updated:', this.currentJointSection);
                              //this.articles = this.currentCrossSection.articles || [];
                          }
                      }
                  }
  
                  
              },
              error: (error: any) => {
                  console.error('Error updating rule:', error);
                  this.toastService.show('Error updating rule: ' + error.message, {
                      classname: 'bg-danger text-light',
                      delay: 3000
                  });
                  this.loading = false;
              }
          });
      }
  
    
  
      
  
     
  
      
  
      // TrackBy functions for better change detection performance
      trackBySectionId(index: number, section: JointSectionModel): string {
          return section.id || section.id || `section-${index}`;
      }
  
      trackByJointSectionId(index: number, jointSection: JointSectionModel): string {
          return jointSection.id || `jointSection-${index}`;
      }
  
      
  
      private transformRuleToCamelCase(rule: any): JointRuleModel {
          console.log('Transforming rule to camelCase:', rule);
          return {
              id: rule.Id || rule.id,
              ruleId: rule.RuleId || rule.ruleId,
              name: rule.Name || rule.name,
              description: rule.Description || rule.description,
              systemId: rule.SystemId || rule.systemId,
  
              jointSections: (rule.JointSections || rule.jointSections || []).map((cs: any) => ({
                  id: cs.Id || cs.id,
                  description: cs.Description || cs.description,
                  material: cs.Material || cs.material,
                  type: cs.Type || cs.type,
                  imageUrl: cs.ImageUrl || cs.imageUrl,
                  articleIds: cs.ArticleIds || cs.articleIds,
                  articles: (cs.Articles || cs.articles || []).map((a: any) => this.transformArticle(a))
              })),
  
            polyline: this.transformPolyline(rule.Polyline || rule.polyline),

            createdAt: rule.CreatedAt || rule.createdAt,
            updatedAt: rule.UpdatedAt || rule.updatedAt
        };
    }

    

    // Transform article object from PascalCase to camelCase
    private transformArticle(article: any): any {
        if (!article) return article;
        if (typeof article !== 'object') return article;

        // Check if already in camelCase
        if (article.articleName !== undefined || article.id !== undefined) {
            return article;
        }

        // Transform from PascalCase
        return {
            id: article.Id || article.id,
            articleName: article.ArticleName || article.articleName,
            material: article.Material || article.material,
            type: article.Type || article.type,
            systemIds: article.SystemIds || article.systemIds,
            systems: (article.Systems || article.systems || []).map((system: any) => this.transformSystem(system)),
            createdAt: article.CreatedAt || article.createdAt,
            updatedAt: article.UpdatedAt || article.updatedAt
        };
    }

    // Transform system object from PascalCase to camelCase (if needed for nested systems)
    private transformSystem(system: any): any {
        if (!system) return system;
        if (typeof system !== 'object') return system;

        // Check if already in camelCase
        if (system.name !== undefined || system.id !== undefined) {
            return system;
        }

        // Basic transformation - only transform if needed
        return {
            id: system.Id || system.id,
            name: system.Name || system.name,
            material: system.Material || system.material,
            type: system.Type || system.type
        };
    }

    startEditing(jointSection: any, field: string) {
          this.editingField = jointSection.id + '-' + field;
      }
  
      stopEditing(jointSection: any) {
          this.editingField = null;
  
          // Auto-save when user clicks outside
          this.saveRule();
      }
  
      showArticleInformation(jointSection: JointSectionModel | null | undefined): void {
          console.log('Show article information for jointSection:', jointSection);
          this.currentJointSection = jointSection || null;
          // Ensure articles array exists on the cross section and use the SAME reference
          if (this.currentJointSection && !this.currentJointSection.articles) {
              this.currentJointSection.articles = [];
          }
          this.articles = [];
          // Point component-level articles to the same array (no copy)
          this.currentJointSection?.articleIds?.forEach(element => {
            this.articlesService.getArticleByName(element).subscribe({
              next: (article) => {
                const transformedArticle = this.transformArticle(article);
               this.articles.push(transformedArticle);
              }
            });
          });
          
        //   this.articles = this.currentJointSection?.articles || [];
          this.selectedSystem = this.systems.find(s => s.id === this.rule?.systemId) || null;
          console.log('Selected System on Submit:', this.selectedSystem);
  
  
          this.showArticleModal = true;
      }
      closeArticleModal(): void {
          this.showArticleModal = false;
      }
  
      addArticle() {
          this.isAddingArticle = true;
  
          this.newArticle = {
              id: '',
              articleName: '',
              material: '',
              type: '',
              systemIds: [],
              systems: []
          };
      }
  
      updateArticle(index: number, field: string, event: any) {
          const value = (event && event.target) ? event.target.value?.trim() ?? '' : (event ?? '');
  
          if (!this.currentJointSection) return;
  
          // Defensive check
          if (!this.currentJointSection.articles || !this.currentJointSection.articles[index]) return;
  
          // Update the article field directly on the model
          (this.currentJointSection.articles[index] as any)[field] = value;
  
          // Optional: mark the rule dirty, or set a local boolean "isDirty = true"
      }
  
  
      removeArticle(index: number) {
          if (!this.currentJointSection) return;
  
          if (!this.currentJointSection.articleIds || !this.currentJointSection.articleIds[index]) return;
           const id = this.articles[index].id;
          this.articles.splice(index, 1);  // remove from UI table
          //this.currentJointSection.articles.splice(index, 1);
          this.currentJointSection.articleIds=[]
          this.articles.forEach(article => {
            this.currentJointSection?.articleIds?.push(article.articleName!);
          });
          this.articlesService.deleteArticle(id!).subscribe({
              next: () => {
                  console.log('Article deleted successfully');
              }  
          });
           this.saveRule();
          //this.currentJointSection.articleIds = this.getArticleIdsArray(this.currentJointSection); // sync articleIds
          
  
  
      }
  
      startEditingArticle(index: number, field: string) {
          this.editingField = `art-${index}-${field}`;
      }
  
      stopEditingArticle(index: number, field: string) {
          this.editingField = null;
          this.saveUpdatedArticle(index);
      }
  
      saveUpdatedArticle(index: number) {
          const updatedArticle = { ...this.articles[index] };
  
          if (!updatedArticle.id) {
              console.warn('Article ID is undefined; skipping update');
              return;
          }
  
          this.articlesService.updateArticle(updatedArticle.id, updatedArticle)
              .subscribe({
                  next: (response) => {
                      console.log('Article updated successfully:', response);
                      const normalizedArticle = this.normalizeArticleResponse(response);
                      this.articles[index] = normalizedArticle;  // replace with saved article
                      this.saveRule();
                  }
              });
      }
  
      private normalizeArticleResponse(apiResponse: any) {
          if (!apiResponse) return apiResponse;
  
          return {
              id: apiResponse.Id,
              articleName: apiResponse.ArticleName,
              material: apiResponse.Material,
              type: apiResponse.Type,
              systemIds: apiResponse.SystemIds,
              systems: apiResponse.Systems,
              createdAt: apiResponse.CreatedAt,
              updatedAt: apiResponse.UpdatedAt
          };
      }
  
      saveNewArticle() {
          if (!this.newArticle.articleName) {
              alert("All fields required");
              return;
          }
          this.newArticle.systemIds?.push(this.rule?.systemId || '');
          this.articlesService.createArticle(this.newArticle)
              .subscribe(created => {
                  const normalizedArticle = this.normalizeArticleResponse(created);
                  this.articles.push(normalizedArticle);  // add to UI table
                  if (this.currentJointSection) {
                      this.currentJointSection.articles = this.articles; // ensure crossSection articles updated
                      this.currentJointSection.articleIds?.push(normalizedArticle.articleName); // sync articleIds
                      this.saveRule();
                  }
                  this.isAddingArticle = false;
              });
      }
  
      cancelAddArticle() {
          this.isAddingArticle = false;
      }
  
      getSystems(): void {
          const params: QueryParams = {
              page: 1
          };
          this.systemsService.getSystems(params).subscribe({
              next: (response: SystemModelPagedResponseModel | any) => {
                  // Handle both camelCase (new) and PascalCase (old) formats
                  const data = response.data || response.Data || [];
                  // Transform systems from PascalCase to camelCase if needed
                  this.systems = Array.isArray(data)
                      ? data.map(system => this.transformSystemIfNeeded(system))
                      : [];
                  console.log('Loaded systems:', this.systems);
  
              },
              error: (error) => {
                  console.error('Error loading systems:', error);
                  this.showError('Could not load systems.');
              }
          });
      }
  
      // Transform system object from PascalCase to camelCase if needed
      private transformSystemIfNeeded(system: any): SystemModel {
          if (!system) return system;
          if (typeof system !== 'object') return system;
  
          // Check if already in camelCase (has lowercase properties)
          if (system.name !== undefined || system.id !== undefined) {
              // Already in camelCase, but check nested objects and return a new object to avoid mutation
              const transformed: SystemModel = {
                  ...system,
                  regions: system.regions && Array.isArray(system.regions)
                      ? system.regions.map((region: any) => this.transformRegionIfNeeded(region))
                      : system.regions
              };
              return transformed;
          }
  
          // Transform from PascalCase to camelCase
          const transformed: SystemModel = {
              id: system.Id || system.id,
              name: system.Name || system.name || '',
              version: system.Version || system.version,
              description: system.Description || system.description,
              material: system.Material || system.material,
              type: system.Type || system.type,
              regions: (system.Regions || system.regions || []).map((region: any) => this.transformRegionIfNeeded(region)),
              articleIds: system.ArticleIds || system.articleIds,
              articles: system.Articles || system.articles,
              createdAt: system.CreatedAt || system.createdAt,
              updatedAt: system.UpdatedAt || system.updatedAt
          };
  
          return transformed;
      }
  
      // Transform region object from PascalCase to camelCase if needed
      private transformRegionIfNeeded(region: any): any {
          if (!region) return region;
          if (typeof region !== 'object') return region;
  
          // Check if already in camelCase
          if (region.country !== undefined || region.id !== undefined) {
              return region;
          }
  
          // Transform from PascalCase to camelCase
          return {
              id: region.Id || region.id,
              country: region.Country || region.country,
              city: region.City || region.city,
              state: region.State || region.state,
              district: region.District || region.district,
              place: region.Place || region.place
          };
      }
  
      private showError(message: string): void {
          this.errorMessage = message;
          this.toastService.show(this.errorMessage, { classname: 'bg-danger text-light', delay: 2000 });
      }
  
  
      showJointSectionInformation(): void {
          this.positionX = 0;
          this.positionY = 0;
          this.positionZ = 0;
          this.scaleX = 1;
          this.scaleY = 1;
          this.rotation = 0;
          this.isMirrored = false;
          this.isNewJointSection = true;
          this.currentJointSection = {
              id: '',
              description: '',
              material: '',
              type: '',
              articles: [],
              articleIds: [],
              imageUrl: '',
             
          } as JointSectionModel;
  
          // load modal fields
  
  
          this.articles = this.currentJointSection.articles || [];
  
          this.showJointSectionModal = true;
      }
  
      closeJointSectionModal(): void {
          // we simply close the modal — if editing we kept object reference so changes remain
          this.showJointSectionModal = false;
          this.currentJointSection = null;
          this.isAddingArticle = false;
          this.isNewJointSection = false;
      }
  
      // Save cross section (supports Add & Edit). Save button will already be disabled when invalid.
      saveJointSection(): void {
          if (!this.currentJointSection) return;
  
         
          // ensure rule exists
          if (!this.rule) this.rule = {} as JointRuleModel;
          if (!this.rule.jointSections) this.rule.jointSections = [];
  
          if (this.isNewJointSection) {
              // add new cross-section reference to rule
              this.rule?.jointSections.push(this.currentJointSection);
          } else {
              // edit mode — the changes already reflect on the object reference in rule
              // optionally you could find and replace the cross-section by id if needed
          }
  
          // sync articleIds
          this.currentJointSection.articleIds = this.getArticleIdsArray(this.currentJointSection);
  
          // persist the whole rule
          this.saveRule();
  
          // close modal
          this.showJointSectionModal = false;
          this.currentJointSection = null;
          this.isNewJointSection = false;
      }
  
      // ---------- Validation (controls Save button enabled state) ----------
      isJointSectionValid(): boolean {
          if (!this.currentJointSection) return false;
  
          const descOk = !!(this.currentJointSection.description && this.currentJointSection.description.toString().trim().length > 0);
          const matOk = !!(this.currentJointSection.material && this.currentJointSection.material.toString().trim().length > 0);
          const typeOk = !!(this.currentJointSection.type && this.currentJointSection.type.toString().trim().length > 0);
  
  
  
          return descOk && matOk && typeOk;
      }
  
      // Sorting methods
      onSort(column: string): void {
          if (!this.rule || !this.rule.jointSections) return;
          
          if (this.sortColumn === column) {
              this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
          } else {
              this.sortColumn = column;
              this.sortDirection = 'asc';
          }
          this.applySorting();
      }
  
      applySorting(): void {
          if (!this.rule || !this.rule.jointSections || !this.sortColumn) {
              return;
          }
  
          this.rule.jointSections.sort((a, b) => {
              let aValue: any;
              let bValue: any;
  
              switch (this.sortColumn) {
                  case 'id':
                      aValue = (a.id || '').toLowerCase();
                      bValue = (b.id || '').toLowerCase();
                      break;
                  case 'description':
                      aValue = (a.description || '').toLowerCase();
                      bValue = (b.description || '').toLowerCase();
                      break;
                  case 'material':
                      aValue = (a.material || '').toLowerCase();
                      bValue = (b.material || '').toLowerCase();
                      break;
                  case 'type':
                      aValue = (a.type || '').toLowerCase();
                      bValue = (b.type || '').toLowerCase();
                      break;
                  default:
                      return 0;
              }
  
              if (aValue < bValue) {
                  return this.sortDirection === 'asc' ? -1 : 1;
              }
              if (aValue > bValue) {
                  return this.sortDirection === 'asc' ? 1 : -1;
              }
              return 0;
          });
      }
  
      getSortIcon(column: string): string {
          if (this.sortColumn !== column) {
              return 'bi-arrow-down-up';
          }
          return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
      }
  
    // Polyline methods
    private transformPolyline(polyline: any): PolylineModel | undefined {
        if (!polyline) return undefined;
        
        return {
            segments: (polyline.Segments || polyline.segments || []).map((seg: any) => ({
                startPoint: { x: seg.StartPoint?.X || seg.startPoint?.x || 0, y: seg.StartPoint?.Y || seg.startPoint?.y || 0 },
                endPoint: { x: seg.EndPoint?.X || seg.endPoint?.x || 0, y: seg.EndPoint?.Y || seg.endPoint?.y || 0 },
                length: seg.Length || seg.length || 0
            })),
            nodes: (polyline.Nodes || polyline.nodes || []).map((node: any) => ({
                point: { x: node.Point?.X || node.point?.x || 0, y: node.Point?.Y || node.point?.y || 0 },
                index: node.Index || node.index || 0
            })),
            totalLength: polyline.TotalLength || polyline.totalLength || 0,
            articleIds: polyline.ArticleIds || polyline.articleIds || [],
            segmentCount: polyline.SegmentCount || polyline.segmentCount || 0,
            nodeCount: polyline.NodeCount || polyline.nodeCount || 0,
            isClosed: polyline.IsClosed || polyline.isClosed || false
        };
    }

    showPolylineInformation(): void {
        if (!this.rule?.polyline) {
            this.currentPolyline = {
                segments: [],
                nodes: [],
                totalLength: 0,
                articleIds: [],
                segmentCount: 0,
                nodeCount: 0,
                isClosed: false
            };
        } else {
            this.currentPolyline = { ...this.rule.polyline };
        }
        this.showPolylineModal = true;
    }

    closePolylineModal(): void {
        this.showPolylineModal = false;
        this.currentPolyline = null;
        this.isAddingSegment = false;
        this.isAddingNode = false;
    }

    savePolyline(): void {
        if (!this.currentPolyline || !this.rule) return;
        
        // Recalculate computed properties
        this.updatePolylineTotals();
        
        this.rule.polyline = this.currentPolyline;
        this.saveRule();
        this.closePolylineModal();
    }

    addSegment(): void {
        this.isAddingSegment = true;
        this.newSegment = {
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 0, y: 0 },
            length: 0
        };
    }

    saveNewSegment(): void {
        // Calculate length
        const dx = this.newSegment.endPoint.x - this.newSegment.startPoint.x;
        const dy = this.newSegment.endPoint.y - this.newSegment.startPoint.y;
        this.newSegment.length = Math.sqrt(dx * dx + dy * dy);
        
        if (!this.currentPolyline) return;
        if (!this.currentPolyline.segments) this.currentPolyline.segments = [];
        
        this.currentPolyline.segments.push({ ...this.newSegment });
        this.updatePolylineTotals();
        this.isAddingSegment = false;
    }

    cancelAddSegment(): void {
        this.isAddingSegment = false;
    }

    removeSegment(index: number): void {
        if (!this.currentPolyline?.segments) return;
        this.currentPolyline.segments.splice(index, 1);
        this.updatePolylineTotals();
    }

    updateSegmentLength(index: number): void {
        if (!this.currentPolyline?.segments || !this.currentPolyline.segments[index]) return;
        
        const segment = this.currentPolyline.segments[index];
        const dx = segment.endPoint.x - segment.startPoint.x;
        const dy = segment.endPoint.y - segment.startPoint.y;
        segment.length = Math.sqrt(dx * dx + dy * dy);
        
        this.updatePolylineTotals();
    }

    updatePolylineTotals(): void {
        if (!this.currentPolyline) return;
        
        // Update segment count
        this.currentPolyline.segmentCount = this.currentPolyline.segments?.length || 0;
        
        // Update node count
        this.currentPolyline.nodeCount = this.currentPolyline.nodes?.length || 0;
        
        // Update total length
        this.currentPolyline.totalLength = this.currentPolyline.segments?.reduce((sum, seg) => sum + seg.length, 0) || 0;
        
        // Check if closed
        if (this.currentPolyline.nodes && this.currentPolyline.nodes.length >= 2) {
            const first = this.currentPolyline.nodes[0].point;
            const last = this.currentPolyline.nodes[this.currentPolyline.nodes.length - 1].point;
            const tolerance = 1e-10;
            this.currentPolyline.isClosed = Math.abs(first.x - last.x) < tolerance && Math.abs(first.y - last.y) < tolerance;
        } else {
            this.currentPolyline.isClosed = false;
        }
    }

    addNode(): void {
        this.isAddingNode = true;
        const nextIndex = this.currentPolyline?.nodes?.length || 0;
        this.newNode = {
            point: { x: 0, y: 0 },
            index: nextIndex
        };
    }

    saveNewNode(): void {
        if (!this.currentPolyline) return;
        if (!this.currentPolyline.nodes) this.currentPolyline.nodes = [];
        
        this.currentPolyline.nodes.push({ ...this.newNode });
        this.updatePolylineTotals();
        this.isAddingNode = false;
    }

    cancelAddNode(): void {
        this.isAddingNode = false;
    }

    removeNode(index: number): void {
        if (!this.currentPolyline?.nodes) return;
        this.currentPolyline.nodes.splice(index, 1);
        // Reindex remaining nodes
        this.currentPolyline.nodes.forEach((node, idx) => {
            node.index = idx;
        });
        this.updatePolylineTotals();
    }

    // SVG scaling functions for polyline visualization
    svgScaleX(x: number): number {
        if (!this.currentPolyline?.segments || this.currentPolyline.segments.length === 0) return 200;
        
        // Find min and max X values
        const allX = this.currentPolyline.segments.flatMap(seg => [seg.startPoint.x, seg.endPoint.x]);
        const minX = Math.min(...allX);
        const maxX = Math.max(...allX);
        const rangeX = maxX - minX || 1;
        
        // Scale to fit in SVG (with padding)
        const padding = 20;
        const svgWidth = 400 - (2 * padding);
        return padding + ((x - minX) / rangeX) * svgWidth;
    }

    svgScaleY(y: number): number {
        if (!this.currentPolyline?.segments || this.currentPolyline.segments.length === 0) return 110;
        
        // Find min and max Y values
        const allY = this.currentPolyline.segments.flatMap(seg => [seg.startPoint.y, seg.endPoint.y]);
        const minY = Math.min(...allY);
        const maxY = Math.max(...allY);
        const rangeY = maxY - minY || 1;
        
        // Scale to fit in SVG (with padding, inverted Y for SVG coordinate system)
        const padding = 20;
        const svgHeight = 220 - (2 * padding);
        return padding + ((maxY - y) / rangeY) * svgHeight; // Invert Y axis
    }

  
    updatePolylineArticleIds(value: string): void {  
        if (!this.currentPolyline) return;  
        this.currentPolyline.articleIds = value.split(',').map(id => id.trim()).filter(id => id);  
    }  
} 
